import {
    ApolloClient,
    ApolloLink,
    from,
    concat,
    Observable,
    NormalizedCacheObject,
    DocumentNode,
} from '@apollo/client/core';
import {
    getMeta,
    getMetaOrFail,
    getMetaBool,
    getMetaNumber,
    getMetaIn,
} from '@precon-tools-suite-by-shared/browser-utils';
import {
    makeHttpLink,
    makeGraphQLWsLink,
    makeCache,
    makePusherLink,
    getHeadersProvider,
    prettyErrorLink,
    makeBearerTokenLink,
    router,
    lazy,
    isSubscription,
    RoundTripLink,
    ApplicationInsightsLink,
    type HeadersOptions,
    type RetryLinkOptions,
} from '@precon-tools-suite-by-shared/shared-graphql-client-js-apollo';
import type { Options as PusherOptions } from 'pusher-js';
import type { IApplicationInsights } from '@microsoft/applicationinsights-web';
import { FragmentRegistryAPI } from '@apollo/client/cache';

function createBackendHttpLink(headers: HeadersOptions): ApolloLink {
    return concat(
        makeBearerTokenLink(headers),
        makeHttpLink({
            url: '/graphql',
            batch: {
                enabled: getMetaBool('graphql-batch-enabled'),
                max: getMetaNumber('graphql-batch-max'),
                interval: getMetaNumber('graphql-batch-interval'),
                debounce: getMetaBool('graphql-batch-debounce'),
            },
        })
    );
}

function createBackendGraphqlWsLink(headers: HeadersOptions): ApolloLink {
    return makeGraphQLWsLink({
        url: getMeta('graphql-ws-endpoint') ?? '/graphql',
        debugLogs: getMetaBool('graphql-log-ws-events'),
        ...headers,
    });
}

function createBackendPusherLink(headers: HeadersOptions): ApolloLink {
    return makePusherLink(getMetaOrFail('graphql-pusher-key'), {
        cluster: getMeta('graphql-pusher-cluster') ?? 'eu',
        disableStats: getMetaBool('graphql-pusher-disable-stats'),
        enabledTransports: getMeta('graphql-pusher-enable-transports')?.split(
            ' '
        ) as PusherOptions['enabledTransports'],
        forceTLS: getMetaBool('graphql-pusher-force-tls'),
        httpHost: getMeta('graphql-pusher-http-host'),
        httpPort: getMetaNumber('graphql-pusher-http-port'),
        httpsPort: getMetaNumber('graphql-pusher-https-port'),
        wsHost: getMeta('graphql-pusher-ws-host'),
        wsPort: getMetaNumber('graphql-pusher-ws-port'),
        wssPort: getMetaNumber('graphql-pusher-wss-port'),
        channelAuthorization: {
            transport: 'ajax',
            endpoint: getMetaOrFail('graphql-subscription-auth-endpoint'),
            headersProvider: getHeadersProvider(headers),
        },
    });
}

function createBackendLogLink(
    appInsights?: IApplicationInsights | null,
    logGraphQLErrors?: boolean,
    logGraphQLRoundTrip?: boolean
): ApolloLink {
    const links: ApolloLink[] = [];

    if (appInsights) {
        links.push(new ApplicationInsightsLink(appInsights));
    }

    if (logGraphQLErrors) {
        links.push(prettyErrorLink);
    }
    if (logGraphQLRoundTrip) {
        links.push(new RoundTripLink());
    }

    return from(links);
}

export type SubscribeRoute =
    | 'graphql-transport-ws'
    | 'graphql-ws'
    | 'pusher'
    | 'ignore';

function createBackendSubscribeLink(headers: HeadersOptions): ApolloLink {
    const wsLink = createBackendGraphqlWsLink(headers);

    return router<SubscribeRoute>(
        'subscribe',
        {
            'graphql-transport-ws': wsLink,
            'graphql-ws': wsLink,
            pusher: lazy(() => createBackendPusherLink(headers)),
            ignore: () => Observable.from([]),
        },
        (op) => {
            if (isSubscription(op)) {
                const via =
                    getMetaIn('graphql-subscription-protocol', [
                        'graphql-transport-ws',
                        'graphql-ws',
                        'pusher',
                        'http',
                        'ignore',
                    ]) ?? null;
                if (via === 'http') return null;
                return via;
            }
            return null;
        }
    );
}

export type CreateBackendApolloClientOptions = HeadersOptions & {
    appInsights?: IApplicationInsights;
    appVersion?: string;
    appName?: string;
    graphqlFragments?: FragmentRegistryAPI | DocumentNode[];
    connectToDevTools?: boolean;
    logGraphQLErrors?: boolean;
    logGraphQLRoundTrip?: boolean;
    retryOptions?: RetryLinkOptions;
};

export function createBackendApolloClient(
    options: CreateBackendApolloClientOptions = {}
): ApolloClient<NormalizedCacheObject> {
    return new ApolloClient({
        cache: makeCache({
            fragments: options.graphqlFragments,
        }),
        link: from([
            createBackendLogLink(
                options.appInsights,
                options.logGraphQLErrors,
                options.logGraphQLRoundTrip
            ),
            createBackendSubscribeLink(options),
            createBackendHttpLink(options),
            // createRetryLink(),
        ]),
        connectToDevTools: options.connectToDevTools,
        name: options.appName,
        version: options.appVersion,
    });
}
